import { createSignal, createComputed } from "react-alien-signals";
import {
  type Document,
  KnowledgeType,
  type Qa,
  type IPagination
} from "@/lib/api/schema";

export enum TrainingTipType {
  Loading = "loading",
  Normal = "normal",
  Error = "error",
  Success = "success"
}

export const initTrainingStore = createSignal<{
  documents: Document[];
  isFirstLoading: boolean;
  isPaginationLoading: boolean;
  pageIndex: number;
  pageSize: number;
  total: number;
  progress: number;
  voiceProgress: number;
  isUpdate: boolean;
  searchQuery: string;
  statusFilter: string[];
  tip: {
    type: TrainingTipType;
    text: string;
    open: boolean;
    total: number;
  };
  // 添加删除操作跟踪
  deletingIds: Set<string>;
  pendingDeleteCount: number;
  savedTotal: number;
  // 用户是否手动关闭了提示
  userClosedTip: boolean;
}>({
  documents: [],
  isFirstLoading: true,
  isPaginationLoading: false,
  pageIndex: 0,
  pageSize: 20,
  total: 0,
  progress: 100,
  voiceProgress: 100,
  searchQuery: "",
  statusFilter: [],
  tip: {
    type: TrainingTipType.Normal,
    text: "",
    open: false,
    total: 0
  },
  isUpdate: false,
  deletingIds: new Set(),
  pendingDeleteCount: 0,
  savedTotal: 0,
  userClosedTip: false
});

export const totalPage = createComputed(() =>
  Math.ceil(initTrainingStore().total / initTrainingStore().pageSize)
);

export interface QaForm {
  title: string;
  questions: string[];
  answer: string;
}

export interface EditingQa extends QaForm {
  id: string;
}

export const initTrainingDrawerStore = createSignal<{
  open: boolean;
  trainingType: KnowledgeType;
  isImprove: boolean;
  isQaFormExpanded: boolean;
  isEditingQa: boolean;
  editingQa: EditingQa | null;
  qa: Qa;
  qaForm: QaForm;
}>({
  open: false,
  isImprove: false,
  isQaFormExpanded: true,
  isEditingQa: false,
  editingQa: null,
  trainingType: KnowledgeType.Website,
  qa: {
    question: "",
    answer: ""
  },
  qaForm: {
    title: "",
    questions: [""],
    answer: ""
  }
});

// 核心状态更新方法
export const updateTrainingStore = (
  updates: Partial<{
    documents: Document[];
    isFirstLoading: boolean;
    isPaginationLoading: boolean;
    pageIndex: number;
    pageSize: number;
    total: number;
    progress: number;
    voiceProgress: number;
    tip: {
      type: TrainingTipType;
      text: string;
      open: boolean;
      total: number;
    };
    isUpdate: boolean;
    deletingIds: Set<string>;
    pendingDeleteCount: number;
    savedTotal: number;
    userClosedTip: boolean;
  }>
) => {
  initTrainingStore({ ...initTrainingStore(), ...updates });
};

// 简化的状态管理方法
export const updateDocuments = (documents: Document[], total?: number) => {
  const updates: any = { documents };
  if (total !== undefined) updates.total = total;
  updateTrainingStore(updates);
};

export const updateProgress = (progress: number) => {
  updateTrainingStore({ progress });
};

export const updatePagination = (pageIndex?: number, pageSize?: number) => {
  const updates: any = {};
  if (pageIndex !== undefined) updates.pageIndex = pageIndex;
  if (pageSize !== undefined) {
    updates.pageSize = pageSize;
    updates.pageIndex = 0; // 重置页码
  }
  updateTrainingStore(updates);
};

export const updateLoadingState = (loading: {
  isFirstLoading?: boolean;
  isPaginationLoading?: boolean;
  isUpdate?: boolean;
}) => {
  updateTrainingStore(loading);
};

// 提示相关方法
export const showTip = (
  type: TrainingTipType,
  text: string,
  total: number = 0
) => {
  updateTrainingStore({
    tip: { type, text, open: true, total }
  });
};

export const closeTip = (userClosed: boolean = false) => {
  const currentStore = initTrainingStore();
  updateTrainingStore({
    tip: { ...currentStore.tip, open: false },
    userClosedTip: userClosed
  });
};

// 便捷的提示方法
export const showSuccessTip = (text: string, total: number = 0) =>
  showTip(TrainingTipType.Success, text, total);

export const showErrorTip = (text: string) =>
  showTip(TrainingTipType.Error, text);

export const showNormalTip = (text: string, total: number = 0) =>
  showTip(TrainingTipType.Normal, text, total);

// 文档状态更新
export const updateDocumentStatus = (id: string, status: any) => {
  const currentStore = initTrainingStore();
  updateTrainingStore({
    documents: currentStore.documents.map((doc) =>
      doc.id === id ? { ...doc, indexing_status: status } : doc
    )
  });
};

// 删除操作管理
export const manageDeleting = (
  action: "mark" | "unmark",
  ids: string | string[]
) => {
  const currentStore = initTrainingStore();
  const newDeletingIds = new Set(currentStore.deletingIds);
  const idArray = Array.isArray(ids) ? ids : [ids];

  if (action === "mark") {
    idArray.forEach((id) => newDeletingIds.add(id));
    updateTrainingStore({
      deletingIds: newDeletingIds,
      pendingDeleteCount: currentStore.pendingDeleteCount + idArray.length
    });
  } else {
    idArray.forEach((id) => newDeletingIds.delete(id));
    updateTrainingStore({
      deletingIds: newDeletingIds,
      pendingDeleteCount: Math.max(
        0,
        currentStore.pendingDeleteCount - idArray.length
      )
    });
  }
};

// 便捷的删除标记方法
export const markDocumentsAsDeleting = (ids: string | string[]) =>
  manageDeleting("mark", ids);

export const unmarkDocumentsAsDeleting = (ids: string | string[]) =>
  manageDeleting("unmark", ids);

// 智能更新文档列表（考虑删除状态）
export const updateDocumentsSafely = (
  documents: Document[],
  serverTotal: number
) => {
  const currentStore = initTrainingStore();

  // 过滤正在删除的文档
  const filteredDocuments = documents.filter(
    (doc) => !currentStore.deletingIds.has(doc.id)
  );

  // 调整总数
  const adjustedTotal = Math.max(
    0,
    serverTotal - currentStore.pendingDeleteCount
  );

  updateDocuments(filteredDocuments, adjustedTotal);
};

// Reset store when switching agents
export const resetTrainingStore = () => {
  initTrainingStore({
    documents: [],
    isFirstLoading: true,
    isPaginationLoading: false,
    pageIndex: 0,
    pageSize: 20,
    total: 0,
    progress: 100,
    voiceProgress: 100,
    searchQuery: "",
    statusFilter: [],
    tip: {
      type: TrainingTipType.Normal,
      text: "",
      open: false,
      total: 0
    },
    isUpdate: false,
    deletingIds: new Set(),
    pendingDeleteCount: 0,
    savedTotal: 0,
    userClosedTip: false
  });
};

// TrainingSourceDrawer state management functions
export const updateTrainingDrawerStore = (
  updates: Partial<{
    open: boolean;
    trainingType: KnowledgeType;
    isImprove: boolean;
    isQaFormExpanded: boolean;
    isEditingQa: boolean;
    editingQa: EditingQa | null;
    qa: Qa;
    qaForm: QaForm;
  }>
) => {
  initTrainingDrawerStore({ ...initTrainingDrawerStore(), ...updates });
};

export const setDrawerOpen = (open: boolean) => {
  updateTrainingDrawerStore({ open });
};

export const setTrainingType = (trainingType: KnowledgeType) => {
  updateTrainingDrawerStore({ trainingType });
};

export const updateQaData = (qa: Partial<Qa>) => {
  const currentStore = initTrainingDrawerStore();
  updateTrainingDrawerStore({
    qa: { ...currentStore.qa, ...qa }
  });
};

export const resetQaData = () => {
  updateTrainingDrawerStore({
    qa: { question: "", answer: "" }
  });
};

export const updateQaFormData = (qaForm: Partial<QaForm>) => {
  const currentStore = initTrainingDrawerStore();
  updateTrainingDrawerStore({
    qaForm: { ...currentStore.qaForm, ...qaForm }
  });
};

export const resetQaFormData = () => {
  updateTrainingDrawerStore({
    isImprove: false,
    qaForm: {
      title: "",
      questions: [""],
      answer: ""
    }
  });
};

export const setQaFormExpanded = (isExpanded: boolean) => {
  updateTrainingDrawerStore({
    isQaFormExpanded: isExpanded
  });
};

export const setEditingQa = (qa: EditingQa | null) => {
  updateTrainingDrawerStore({
    isEditingQa: qa !== null,
    editingQa: qa
  });
};

export const updateEditingQa = (updates: Partial<EditingQa>) => {
  const currentStore = initTrainingDrawerStore();
  if (currentStore.editingQa) {
    updateTrainingDrawerStore({
      editingQa: { ...currentStore.editingQa, ...updates }
    });
  }
};

export const cancelEditingQa = () => {
  updateTrainingDrawerStore({
    isEditingQa: false,
    editingQa: null
  });
};

export const setIsImprove = (isImprove: boolean) => {
  updateTrainingDrawerStore({ isImprove });
};

export const closeDrawerAndReset = () => {
  updateTrainingDrawerStore({
    open: false,
    isImprove: false,
    isEditingQa: false,
    editingQa: null,
    qa: { question: "", answer: "" },
    qaForm: {
      title: "",
      questions: [""],
      answer: ""
    }
  });
};

// 重置 dataMap 状态
export const resetDataMapStore = () => {
  initDataMap({
    [KnowledgeType.Website]: {
      data: [],
      pagination: {
        total: 0,
        pageIndex: 0,
        pageSize: 20
      },
      isLoading: false,
      isTraining: false
    },
    [KnowledgeType.Document]: {
      data: [],
      pagination: {
        total: 0,
        pageIndex: 0,
        pageSize: 20
      },
      isLoading: false,
      isTraining: false
    },
    [KnowledgeType.QA]: {
      data: [],
      pagination: {
        total: 0,
        pageIndex: 0,
        pageSize: 10
      },
      isLoading: false,
      isTraining: false
    }
  });
};

export interface ISource {
  id: string;
  name: string;
  file?: File;
  source_url?: string;
  file_size: number;
  file_type: string;
  questions?: string[];
  created_at?: number;
  title?: string;
  answer: string;
}

export const initDataMap = createSignal<{
  [key in KnowledgeType]: {
    data: ISource[];
    pagination: IPagination;
    isLoading: boolean;
    isTraining: boolean;
  };
}>({
  [KnowledgeType.Website]: {
    data: [],
    pagination: {
      total: 0,
      pageIndex: 0,
      pageSize: 20
    },
    isLoading: false,
    isTraining: false
  },
  [KnowledgeType.Document]: {
    data: [],
    pagination: {
      total: 0,
      pageIndex: 0,
      pageSize: 20
    },
    isLoading: false,
    isTraining: false
  },
  [KnowledgeType.QA]: {
    data: [],
    pagination: {
      total: 0,
      pageIndex: 0,
      pageSize: 10
    },
    isLoading: false,
    isTraining: false
  }
});

// DataMap state management for TrainingSourceDrawer
// Note: These functions work with the initDataMap signal from TrainingSourceDrawer
// They will be imported and used in the component

export const createDataMapHelpers = (dataMapSignal: any) => {
  const updateDataMapForType = (
    knowledgeType: KnowledgeType,
    updates: Partial<{
      data: any[];
      pagination: any;
      isLoading: boolean;
      isTraining: boolean;
    }>
  ) => {
    dataMapSignal((prev: any) => ({
      ...prev,
      [knowledgeType]: {
        ...prev[knowledgeType],
        ...updates
      }
    }));
  };

  const setLoadingState = (
    knowledgeType: KnowledgeType,
    isLoading: boolean
  ) => {
    updateDataMapForType(knowledgeType, { isLoading });
  };

  const setTrainingState = (
    knowledgeType: KnowledgeType,
    isTraining: boolean
  ) => {
    updateDataMapForType(knowledgeType, { isTraining });
  };

  const addDocuments = (knowledgeType: KnowledgeType, documents: any[]) => {
    dataMapSignal((prev: any) => ({
      ...prev,
      [knowledgeType]: {
        ...prev[knowledgeType],
        data: [...documents, ...prev[knowledgeType].data],
        isLoading: false
      }
    }));
  };

  const setDocuments = (
    knowledgeType: KnowledgeType,
    documents: any[],
    isLoading = false
  ) => {
    updateDataMapForType(knowledgeType, {
      data: documents,
      isLoading
    });
  };

  const removeDocumentById = (knowledgeType: KnowledgeType, id: string) => {
    dataMapSignal((prev: any) => ({
      ...prev,
      [knowledgeType]: {
        ...prev[knowledgeType],
        data: prev[knowledgeType].data.filter((item: any) => item.id !== id)
      }
    }));
  };

  const removeDocumentsByIds = (
    knowledgeType: KnowledgeType,
    ids: string[]
  ) => {
    dataMapSignal((prev: any) => ({
      ...prev,
      [knowledgeType]: {
        ...prev[knowledgeType],
        data: prev[knowledgeType].data.filter(
          (item: any) => !ids.includes(item.id)
        )
      }
    }));
  };

  const updatePagination = (
    knowledgeType: KnowledgeType,
    paginationUpdates: any
  ) => {
    dataMapSignal((prev: any) => ({
      ...prev,
      [knowledgeType]: {
        ...prev[knowledgeType],
        pagination: { ...prev[knowledgeType].pagination, ...paginationUpdates }
      }
    }));
  };

  const setPageIndex = (knowledgeType: KnowledgeType, pageIndex: number) => {
    updatePagination(knowledgeType, { pageIndex });
  };

  const setPageSize = (
    knowledgeType: KnowledgeType,
    pageSize: number,
    resetIndex = true
  ) => {
    updatePagination(knowledgeType, {
      pageSize,
      ...(resetIndex && { pageIndex: 0 })
    });
  };

  const handleBatchDelete = (
    knowledgeType: KnowledgeType,
    idsToDelete: string[]
  ) => {
    dataMapSignal((prev: any) => {
      const newData = prev[knowledgeType].data.filter(
        (item: any) => !idsToDelete.includes(item.id)
      );
      const currentPage = prev[knowledgeType].pagination.pageIndex;
      const pageSize = prev[knowledgeType].pagination.pageSize;
      const totalPages = Math.ceil(newData.length / pageSize);

      let newPageIndex = currentPage;
      if (newData.length > 0 && currentPage >= totalPages && currentPage > 0) {
        newPageIndex = totalPages - 1;
      }
      if (newData.length === 0) {
        newPageIndex = 0;
      }

      return {
        ...prev,
        [knowledgeType]: {
          ...prev[knowledgeType],
          data: newData,
          pagination: {
            ...prev[knowledgeType].pagination,
            total: newData.length,
            pageIndex: newPageIndex
          }
        }
      };
    });
  };

  return {
    updateDataMapForType,
    setLoadingState,
    setTrainingState,
    addDocuments,
    setDocuments,
    removeDocumentById,
    removeDocumentsByIds,
    updatePagination,
    setPageIndex,
    setPageSize,
    handleBatchDelete
  };
};

// 处理状态管理
export const updateProcessingState = (state: {
  savedTotal?: number;
  userClosedTip?: boolean;
}) => {
  updateTrainingStore(state);
};

export const resetProcessingState = () => {
  updateProcessingState({
    savedTotal: 0,
    userClosedTip: false
  });
};
